import { useTranslation } from 'react-i18next';
import {
  useMutation,
  useQueryClient,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query';
import {
  ILayout,
  useNotifications,
  configureLayoutQuery,
  DashboardComponentNames,
  IDashboardComponentProperties,
  usePerformancePageStore,
  usePerformanceReportBuilderPageStore,
  getResponseErrorMessage,
  layoutQuery,
} from '@diana/core';
import { PERFORMANCE_REPORT_BUILDER_PAGE_NS } from '../i18n';

type TResult = ILayout<IDashboardComponentProperties, DashboardComponentNames>;

export const usePublishDraftLayoutMutation = (
  options?: UseMutationOptions<TResult, unknown, void>,
): UseMutationResult<TResult, unknown, void> => {
  const { t } = useTranslation(PERFORMANCE_REPORT_BUILDER_PAGE_NS);

  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const getPerformanceReportsQuery = layoutQuery.getPerformanceReports();

  const setReports = usePerformancePageStore((s) => s.setReports);
  const getLayout = usePerformanceReportBuilderPageStore((s) => s.getLayout);

  return useMutation({
    mutationFn: async () => {
      const layout = getLayout();
      if (!layout) throw new Error('layout not found');

      return await configureLayoutQuery.publishDraft({ layoutId: layout.id }).fn({});
    },
    ...options,
    onSuccess: (data, variables, context) => {
      setReports([]);
      queryClient.removeQueries({ queryKey: getPerformanceReportsQuery.key });
      options?.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      notifications.error({
        message: t('labels.publishError'),
        description: getResponseErrorMessage(error),
      });
      options?.onError?.(error, variables, context);
    },
  });
};
